import { useState, useRef, useEffect } from "react";
import confetti from "canvas-confetti";
import { trackEvent } from "../tracking";

const SYMBOLS = ["🎵", "⭐", "🎤", "🎶", "🏆", "💰"];
const JACKPOT = 5; // 💰
const SLOT_H = 88;
const STRIP_LEN = 28;
// Reel stop times — fast enough to feel snappy, staggered for anticipation
const DURATIONS = [800, 1300, 1800];
// CTA slides in 2 seconds after jackpot lands
const JACKPOT_HOLD_MS = 2000;

function makeStrip() {
  const s = [];
  for (let i = 0; i < STRIP_LEN - 1; i++) {
    s.push(Math.floor(Math.random() * SYMBOLS.length));
  }
  s.push(JACKPOT); // always ends on 💰
  return s;
}

const STATUS = [
  "",
  "💰 One down — keep going!",
  "💰 💰 — ONE MORE! SO CLOSE!",
  "💰 💰 💰 THREE OF A KIND!",
];

export default function SlotMachineModal({ onClaim, onSkip, onWhatsApp, eventLabel = "Your exclusive Raleigh deal · May 9 · Enigma Nightclub" }) {
  const [phase, setPhase] = useState("intro"); // intro | spinning | jackpot | revealed
  const [stoppedCount, setStoppedCount] = useState(0);
  const [showCTA, setShowCTA] = useState(false);
  const strips = useRef([makeStrip(), makeStrip(), makeStrip()]);
  const reelRefs = useRef([null, null, null]);

  // Auto-spin 0.8s after modal appears — mirrors "we saved something for you" beat
  useEffect(() => {
    const t = setTimeout(() => spin(), 100);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function spin() {
    setPhase("spinning");
    trackEvent("SpinStarted", { content_name: "slot_machine_spin" });

    strips.current.forEach((strip, i) => {
      const el = reelRefs.current[i];
      if (!el) return;
      const targetY = -(strip.length - 1) * SLOT_H;
      el.style.transition = `transform ${DURATIONS[i]}ms cubic-bezier(0.05, 0.92, 0.18, 1)`;
      el.style.transform = `translateY(${targetY}px)`;
    });

    setTimeout(() => setStoppedCount(1), DURATIONS[0] + 120);
    setTimeout(() => setStoppedCount(2), DURATIONS[1] + 120);
    setTimeout(() => {
      setStoppedCount(3);
      setPhase("jackpot");
      trackEvent("SpinJackpot", { content_name: "slot_machine_win", value: 20 });
      fireConfetti();
      // Hold jackpot for 2s, then slide in CTA
      setTimeout(() => setShowCTA(true), JACKPOT_HOLD_MS);
    }, DURATIONS[2] + 300);
  }

  function fireConfetti() {
    const colors = ["#f59e0b", "#C9ED00", "#ffffff", "#fbbf24"];
    confetti({ particleCount: 100, spread: 80, origin: { y: 0.5 }, colors });
    setTimeout(() => {
      confetti({ particleCount: 60, angle: 60,  spread: 55, origin: { x: 0,   y: 0.6 }, colors });
      confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1,   y: 0.6 }, colors });
    }, 350);
    setTimeout(() => {
      confetti({ particleCount: 80, spread: 120, decay: 0.91, scalar: 1.2, origin: { y: 0.4 }, colors });
    }, 700);
  }

  const isJackpot = phase === "jackpot";
  const isSpinning = phase === "spinning";

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/88 backdrop-blur-sm p-4 pb-6">
      <div className="relative w-full max-w-sm">

        {/* Animated glow border */}
        <div
          className="absolute -inset-[3px] rounded-[2rem] blur-lg transition-all duration-700"
          style={{
            background: isJackpot
              ? "linear-gradient(135deg, #C9ED00, #f59e0b, #C9ED00)"
              : "linear-gradient(135deg, #f59e0b, #fbbf24)",
            opacity: isJackpot ? 0.8 : 0.4,
            animation: isJackpot ? "jackpot-border 1.2s ease-in-out infinite alternate" : "none",
          }}
        />

        <div className="relative bg-[#080808] border border-white/10 rounded-[1.75rem] p-7 overflow-hidden">

          {/* Top shimmer line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

          {/* Header — mirrors the ad copy */}
          {isJackpot ? (
            <div className="text-center mb-6">
              <p
                className="text-xs tracking-[0.35em] text-[#C9ED00] uppercase font-black mb-2"
                style={{ animation: "pulse-text 0.8s ease-in-out infinite alternate" }}
              >
                🏆 JACKPOT!
              </p>
              <h2 className="text-[2.6rem] font-serif text-white leading-tight">
                $20 BONUS<br />UNLOCKED!
              </h2>
              <p className="text-amber-400 text-sm mt-1 font-medium">
                courtesy of <span className="font-black">remit2any</span>
              </p>
            </div>
          ) : (
            <div className="text-center mb-5">
              {/* Directly mirrors the ad line */}
              <h2 className="text-2xl font-serif text-white leading-snug mb-1">
                We saved something<br />
                <span className="text-[#C9ED00]">for you.</span>
              </h2>
              <p className="text-white/30 text-xs mt-2">
                {eventLabel}
              </p>
            </div>
          )}

          {/* Slot reels */}
          <div className="flex justify-center gap-3 mb-3">
            {strips.current.map((strip, i) => {
              const reelStopped = stoppedCount > i;
              return (
                <div
                  key={i}
                  className="relative rounded-2xl overflow-hidden transition-all duration-500"
                  style={{
                    width: SLOT_H,
                    height: SLOT_H,
                    border: isJackpot
                      ? "2px solid #C9ED00"
                      : reelStopped
                      ? "2px solid #f59e0b"
                      : "2px solid rgba(255,255,255,0.1)",
                    boxShadow: isJackpot
                      ? "0 0 28px rgba(201,237,0,0.65), inset 0 0 20px rgba(201,237,0,0.12)"
                      : reelStopped
                      ? "0 0 14px rgba(245,158,11,0.45)"
                      : "none",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div
                    ref={(el) => (reelRefs.current[i] = el)}
                    style={{ transform: "translateY(0px)", willChange: "transform" }}
                  >
                    {strip.map((symIdx, j) => (
                      <div
                        key={j}
                        className="flex items-center justify-center select-none"
                        style={{
                          width: SLOT_H,
                          height: SLOT_H,
                          fontSize: "2.4rem",
                          filter:
                            isJackpot && j === strip.length - 1
                              ? "drop-shadow(0 0 10px rgba(201,237,0,0.9))"
                              : "none",
                        }}
                      >
                        {SYMBOLS[symIdx]}
                      </div>
                    ))}
                  </div>

                  {/* Blur overlay while reel is still spinning */}
                  {isSpinning && !reelStopped && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(to bottom, #080808cc 0%, transparent 30%, transparent 70%, #080808cc 100%)",
                      }}
                    />
                  )}

                  {/* Jackpot lime flash */}
                  {isJackpot && (
                    <div
                      className="absolute inset-0 pointer-events-none rounded-2xl"
                      style={{
                        background: "rgba(201,237,0,0.08)",
                        animation: "jackpot-flash 0.6s ease-in-out infinite alternate",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Status line */}
          <p
            className="text-center text-xs font-bold tracking-wide mb-5 min-h-[18px] transition-all duration-300"
            style={{
              color:
                isJackpot ? "#C9ED00"
                : stoppedCount === 2 ? "#C9ED00"
                : stoppedCount === 1 ? "#f59e0b"
                : "transparent",
              animation:
                stoppedCount === 2 || isJackpot
                  ? "pulse-text 0.7s ease-in-out infinite alternate"
                  : "none",
            }}
          >
            {STATUS[stoppedCount]}
          </p>

          {/* Loading state before spin starts */}
          {phase === "intro" && (
            <div className="w-full py-4 rounded-full text-white/20 text-sm text-center tracking-widest"
              style={{ animation: "pulse-text 0.8s ease-in-out infinite alternate" }}>
              Loading your deal…
            </div>
          )}

          {/* Spinning state */}
          {isSpinning && (
            <div
              className="w-full py-4 rounded-full font-black text-lg text-center text-white/20 border border-white/10"
              style={{ animation: "pulse-text 0.8s ease-in-out infinite alternate" }}
            >
              Revealing your deal…
            </div>
          )}

          {/* Jackpot — proof screenshot fades in immediately */}
          {isJackpot && (
            <div className="mb-4 transition-all duration-500"
              style={{ animation: "fade-up 0.5s ease-out forwards" }}>
              <div className="flex items-center gap-4 bg-white/5 border border-[#C9ED00]/20 rounded-2xl p-4">
                <img
                  src="/remit2any-proof.png"
                  alt="$20 credited to remit2any"
                  className="w-20 rounded-xl flex-shrink-0 shadow-lg"
                />
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-widest mb-1">
                    Real proof
                  </p>
                  <p className="text-white font-bold text-sm leading-tight">
                    $20 credited instantly
                  </p>
                  <p className="text-white/40 text-[11px] mt-0.5">
                    USD Wallet · Apr 29, 2026
                  </p>
                  <p className="text-[10px] text-amber-400/70 mt-1">
                    🛡️ Cross River Bank (FDIC)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CTA slides in after 2s hold */}
          {isJackpot && (
            <div
              className="transition-all duration-500 overflow-hidden"
              style={{
                maxHeight: showCTA ? "320px" : "0px",
                opacity: showCTA ? 1 : 0,
                transform: showCTA ? "translateY(0)" : "translateY(12px)",
              }}
            >
              {/* Prize teaser */}
              <div className="mb-3 px-4 py-3 bg-[#C9ED00]/5 border border-[#C9ED00]/20 rounded-2xl text-center">
                <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Also — powered by remit2any</p>
                <div className="flex justify-center gap-3 mb-2">
                  {["🎧 AirPods", "💻 MacBook Neo", "🎁 Gift Cards"].map((p) => (
                    <span key={p} className="text-[11px] text-white/70 font-bold">{p}</span>
                  ))}
                </div>
                <p className="text-[#C9ED00] text-xs font-black">Show up. You can win one of these.</p>
              </div>

              <button
                onClick={onClaim}
                className="w-full py-4 rounded-full font-black text-base text-black transition-all active:scale-95 hover:opacity-90 mb-2"
                style={{
                  background: "#C9ED00",
                  boxShadow: "0 0 40px rgba(201,237,0,0.55)",
                }}
              >
                CLAIM: Ticket + $20 Bundle →
              </button>
              {onWhatsApp && (
                <button
                  onClick={onWhatsApp}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-black text-sm transition-all active:scale-95 mb-3"
                  style={{ background: "#25D366", color: "#000" }}
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Already Done KYC? Claim via WhatsApp
                </button>
              )}
              <p className="text-center text-[10px] text-white/20 tracking-wide leading-relaxed">
                Buy ticket · Install remit2any · Complete US KYC · Get $20 credited
              </p>
            </div>
          )}

          {/* Skip — only before jackpot */}
          {!isJackpot && (
            <button
              onClick={onSkip}
              className="w-full mt-3 text-center text-[10px] text-white/15 hover:text-white/35 transition-colors"
            >
              skip
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes jackpot-border {
          from { opacity: 0.7; }
          to   { opacity: 1; }
        }
        @keyframes jackpot-flash {
          from { opacity: 0.5; }
          to   { opacity: 1; }
        }
        @keyframes pulse-text {
          from { opacity: 0.6; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
